import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { roleLabel } from "@/lib/roles";
import { useBranding } from "@/context/BrandingContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Search, MoreHorizontal, Pencil, Archive, ArchiveRestore, Trash2, KeyRound, Columns3,
  ShieldOff, ShieldCheck, LockOpen, Copy as CopyIcon, Upload, Check, AlertCircle,
  CheckCircle2,
} from "lucide-react";

const STATUS_TABS = [
  { key: "", label: "Active" },
  { key: "on_leave", label: "On Leave" },
  { key: "resigned", label: "Resigned" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  on_leave: "bg-amber-50 text-amber-700 border-amber-200",
  resigned: "bg-zinc-100 text-zinc-600",
  archived: "bg-zinc-100 text-zinc-500",
};

const EMPTY = {
  name: "", designation: "", department: "Design", branch: "",
  email: "", phone: "", date_of_joining: "",
  salary: "", photo_url: "", reporting_manager_id: "", team_lead_id: "",
  create_erp_login: true, skills: [],
  employee_code: "",
  login_username: "",
  password_mode: "auto",
  initial_password: "",
};

function WelcomeDialog({ open, onClose, data }) {
  if (!data) return null;
  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(`${label} copied`); };
  const rows = [
    { label: "ERP Login", value: data.login_email, testid: "wd-login" },
    { label: "Temporary Password", value: data.temp_password, testid: "wd-password" },
    { label: "ERP Login URL", value: data.login_url, testid: "wd-url" },
  ];
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent data-testid="welcome-dialog" className="max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-5 h-5" />
            <AlertDialogTitle>Employee Account Created Successfully</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 text-zinc-800">
              <div className="text-sm">
                <div className="font-medium text-zinc-900">{data.employee_name}</div>
                {data.employee_code && (
                  <div className="font-mono text-[11px] text-zinc-500">{data.employee_code}</div>
                )}
              </div>
              <div className="border border-zinc-200 rounded-md bg-zinc-50 divide-y divide-zinc-100">
                {rows.map((r) => (
                  <div key={r.label} className="flex items-center gap-3 p-3">
                    <div className="w-40 shrink-0 text-[11px] uppercase tracking-widest text-zinc-500">{r.label}</div>
                    <div className="flex-1 font-mono text-xs break-all" data-testid={`${r.testid}-value`}>{r.value}</div>
                    <button
                      className="p-1.5 rounded-sm hover:bg-white border border-transparent hover:border-zinc-200 text-zinc-500 hover:text-zinc-900"
                      onClick={() => copy(r.value, r.label)}
                      data-testid={`${r.testid}-copy`}
                      aria-label={`Copy ${r.label}`}
                    >
                      <CopyIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-amber-700 flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>The temporary password is shown only once. Copy it now and share with the employee. They'll be forced to change it on first login.</span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} data-testid="wd-done">Done</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PhotoUpload({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image file."); return; }
    if (file.size > 500 * 1024) { toast.error("Image is larger than 500 KB. Please compress or crop."); return; }
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = () => { onChange(reader.result); setBusy(false); };
      reader.onerror = () => { toast.error("Could not read the file."); setBusy(false); };
      reader.readAsDataURL(file);
    } catch { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-full border border-zinc-200 bg-zinc-50 overflow-hidden flex items-center justify-center text-zinc-400 flex-shrink-0" data-testid={`${testid}-preview`}>
        {value
          ? <img src={value} alt="" className="w-full h-full object-cover" />
          : <span className="text-[10px]">No photo</span>}
      </div>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`}>
          <Upload className="w-3.5 h-3.5 mr-1.5" /> {value ? "Change photo" : "Upload photo"}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testid}-remove`}>
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function useEmailAvailability(email, enabled, excludeEmployeeId) {
  const trimmed = (email || "").trim().toLowerCase();
  return useQuery({
    queryKey: ["emp-check-email", trimmed, excludeEmployeeId],
    queryFn: async () => (await api.get(`/employees/check-login-email`, { params: { email: trimmed, exclude_employee_id: excludeEmployeeId } })).data,
    enabled: enabled && trimmed.length >= 3 && trimmed.includes("@"),
    staleTime: 5_000,
  });
}

function useCodeAvailability(code, enabled, excludeEmployeeId) {
  const trimmed = (code || "").trim();
  return useQuery({
    queryKey: ["emp-check-code", trimmed, excludeEmployeeId],
    queryFn: async () => (await api.get(`/employees/check-code`, { params: { code: trimmed, exclude_employee_id: excludeEmployeeId } })).data,
    enabled: enabled && trimmed.length >= 2,
    staleTime: 5_000,
  });
}

function EmployeeForm({ form, setForm, employees, departments, designations, branches, loginDomain, isEdit }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const composedEmail = form.login_username ? `${form.login_username.trim().toLowerCase()}@${loginDomain}` : "";
  const emailCheck = useEmailAvailability(composedEmail, !isEdit && !!form.login_username && form.create_erp_login);
  const emailAvail = emailCheck.data?.available;
  const codeCheck = useCodeAvailability(form.employee_code, !isEdit && !!form.employee_code);
  const codeAvail = codeCheck.data?.available;
  const codeValid = codeCheck.data?.valid_format;
  const teamLeadOptions = useMemo(
    () => employees.filter((e) => ["team_lead", "senior_designer", "operations_manager"].includes(e.role)),
    [employees],
  );
  return (
    <div className="mt-6 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="emp-name" /></div>

        {!isEdit && (
          <div className="col-span-2">
            <Label>Employee Code</Label>
            <Input
              placeholder="Leave empty to auto-generate as EMP-#### · e.g. SRK-001 / DHE-025 / CAD-014"
              value={form.employee_code}
              onChange={(e) => set("employee_code", e.target.value.toUpperCase())}
              data-testid="emp-code"
            />
            {form.employee_code && (
              <div className="mt-1 text-[11px] flex items-center gap-1" data-testid="emp-code-status">
                {codeCheck.isFetching ? <span className="text-zinc-500">Checking…</span> :
                 codeValid === false ? <><AlertCircle className="w-3 h-3 text-red-600" /><span className="text-red-600">Format: letters/digits/hyphens, 2–32 chars</span></> :
                 codeAvail === true ? <><Check className="w-3 h-3 text-emerald-600" /><span className="text-emerald-700">Available</span></> :
                 codeAvail === false ? <><AlertCircle className="w-3 h-3 text-red-600" /><span className="text-red-600">Already in use — pick another</span></> :
                 null}
              </div>
            )}
          </div>
        )}

        <div>
          <Label>Designation</Label>
          {designations.length > 0 ? (
            <Select value={form.designation || ""} onValueChange={(v) => set("designation", v)}>
              <SelectTrigger data-testid="emp-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
              <SelectContent>{designations.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input value={form.designation} onChange={(e) => set("designation", e.target.value)} required data-testid="emp-designation-free" />
          )}
          <div className="text-[11px] text-zinc-500 mt-1">Security role is auto-assigned from the designation. MD can override later from the employee record.</div>
        </div>

        <div>
          <Label>Department</Label>
          {departments.length > 0 ? (
            <Select value={form.department || ""} onValueChange={(v) => set("department", v)}>
              <SelectTrigger data-testid="emp-department"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input value={form.department} onChange={(e) => set("department", e.target.value)} data-testid="emp-department-free" />
          )}
        </div>

        <div>
          <Label>Branch / Team</Label>
          {branches.length > 0 ? (
            <Select value={form.branch || "_none"} onValueChange={(v) => set("branch", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="emp-branch"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">—</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input value={form.branch || ""} onChange={(e) => set("branch", e.target.value)} data-testid="emp-branch-free" />
          )}
        </div>

        <div><Label>Personal email <span className="text-[10px] text-zinc-400 font-normal">(HR contact only · NOT ERP login)</span></Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="e.g. rama@gmail.com — personal, for HR communications" data-testid="emp-email" /></div>
        <div><Label>Mobile</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="emp-phone" /></div>
        <div><Label>Joining date</Label><Input type="date" value={form.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} data-testid="emp-joining" /></div>
        <div><Label>Monthly salary</Label><Input type="number" value={form.salary || ""} onChange={(e) => set("salary", e.target.value)} data-testid="emp-salary" /></div>

        <div>
          <Label>Reporting manager</Label>
          <Select value={form.reporting_manager_id || "_none"} onValueChange={(v) => set("reporting_manager_id", v === "_none" ? "" : v)}>
            <SelectTrigger data-testid="emp-mgr"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="_none">—</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Team lead</Label>
          <Select value={form.team_lead_id || "_none"} onValueChange={(v) => set("team_lead_id", v === "_none" ? "" : v)}>
            <SelectTrigger data-testid="emp-tl"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="_none">—</SelectItem>{teamLeadOptions.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="col-span-2">
          <Label>Photo</Label>
          <PhotoUpload value={form.photo_url || ""} onChange={(v) => set("photo_url", v)} testid="emp-photo" />
        </div>

        {!isEdit && (
          <div className="col-span-2 border-t border-zinc-200 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-medium">ERP Login Account</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">The credentials this employee uses to sign in to the ERP. Separate from their personal email above.</div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.create_erp_login} onChange={(e) => set("create_erp_login", e.target.checked)} data-testid="emp-create-login" />
              <span>Create ERP login for this employee</span>
            </label>

            {form.create_erp_login && (
              <div className="pl-6 space-y-3 border-l-2 border-zinc-100">
                <div>
                  <Label>ERP Login Username</Label>
                  <div className="flex items-stretch">
                    <Input
                      placeholder="e.g. srk (leave empty to auto-generate)"
                      value={form.login_username}
                      onChange={(e) => set("login_username", e.target.value.replace(/[^a-zA-Z0-9._-]/g, "").toLowerCase())}
                      data-testid="emp-login-username"
                      className="rounded-r-none border-r-0"
                    />
                    <div className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-zinc-200 bg-zinc-50 text-zinc-600 text-sm font-mono select-none" data-testid="emp-login-domain">
                      @{loginDomain}
                    </div>
                  </div>
                  {form.login_username && (
                    <div className="mt-1 text-[11px] flex items-center gap-1" data-testid="emp-login-email-status">
                      {emailCheck.isFetching ? <span className="text-zinc-500">Checking availability…</span> :
                       emailAvail === true ? <><Check className="w-3 h-3 text-emerald-600" /><span className="text-emerald-700">{composedEmail} · Available</span></> :
                       emailAvail === false ? <><AlertCircle className="w-3 h-3 text-red-600" /><span className="text-red-600">{composedEmail} · Already in use — pick another</span></> :
                       null}
                    </div>
                  )}
                  {!form.login_username && (
                    <div className="text-[11px] text-zinc-500 mt-1">Will be auto-generated as <span className="font-mono">emp&lt;code&gt;@{loginDomain}</span></div>
                  )}
                </div>

                <div>
                  <Label>Initial password</Label>
                  <div className="flex items-center gap-4 mt-1">
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="pwmode" checked={form.password_mode === "auto"} onChange={() => set("password_mode", "auto")} data-testid="emp-pwmode-auto" />
                      <span>Auto-generate temporary password</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input type="radio" name="pwmode" checked={form.password_mode === "manual"} onChange={() => set("password_mode", "manual")} data-testid="emp-pwmode-manual" />
                      <span>Set manually</span>
                    </label>
                  </div>
                  {form.password_mode === "manual" && (
                    <Input
                      type="text"
                      className="mt-2 font-mono"
                      placeholder="Set a starting password (min 6 chars)"
                      value={form.initial_password}
                      onChange={(e) => set("initial_password", e.target.value)}
                      data-testid="emp-initial-password"
                    />
                  )}
                  <div className="text-[11px] text-zinc-500 mt-1">The employee will be forced to change this on first login regardless.</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Employees() {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const loginDomain = (branding?.login_domain || "dreamhome.com").trim();
  const loginUrl = process.env.REACT_APP_ERP_LOGIN_URL || (typeof window !== "undefined" ? `${window.location.origin}/login` : "");
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");

  // M2-A24 · Column selector — persist to localStorage
  const OPTIONAL_COLS = [
    { key: "joining_date", label: "Joining Date" },
    { key: "reporting_manager", label: "Reporting Manager" },
    { key: "team_lead", label: "Team Lead" },
    { key: "mobile", label: "Mobile" },
    { key: "personal_email", label: "Personal Email" },
    { key: "monthly_salary", label: "Monthly Salary" },
    { key: "company", label: "Company" },
    { key: "division", label: "Division" },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("dh_emp_cols_v1") || "{}"); }
    catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[emp] cols LS parse failed", err); return {}; }
  });
  const toggleCol = (k) => setVisibleCols((v) => {
    const next = { ...v, [k]: !v[k] };
    try { window.localStorage.setItem("dh_emp_cols_v1", JSON.stringify(next)); }
    catch (err) { if (process.env.NODE_ENV !== "production") console.warn("[emp] cols LS write failed", err); }
    return next;
  });
  const totalColCount = 10 + OPTIONAL_COLS.filter((c) => visibleCols[c.key]).length;
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [welcome, setWelcome] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: roles = [] } = useQuery({ queryKey: ["emp-roles"], queryFn: async () => (await api.get("/employees/meta/roles")).data });
  const { data: departments = [] } = useQuery({ queryKey: ["directory", "departments"], queryFn: async () => (await api.get("/directory/departments")).data });
  const { data: designations = [] } = useQuery({ queryKey: ["directory", "designations"], queryFn: async () => (await api.get("/directory/designations")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["directory", "branches"], queryFn: async () => (await api.get("/directory/branches")).data });
  const { data: rows = [] } = useQuery({
    queryKey: ["employees", tab, search, roleFilter, designationFilter, departmentFilter, branchFilter],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (tab) p.set("status", tab); else p.set("status", "active_all");
      if (search) p.set("q", search);
      if (roleFilter) p.set("role", roleFilter);
      if (designationFilter) p.set("designation", designationFilter);
      if (departmentFilter) p.set("department", departmentFilter);
      if (branchFilter) p.set("branch", branchFilter);
      return (await api.get(`/employees?${p.toString()}`)).data;
    },
  });

  const createMut = useMutation({
    mutationFn: (p) => api.post("/employees", p),
    onSuccess: (res) => {
      toast.success(`${res.data.employee_code} created`);
      qc.invalidateQueries({ queryKey: ["employees"] });
      setAddOpen(false); setForm(EMPTY);
      if (res.data.temp_password) {
        setWelcome({
          employee_name: res.data.name,
          employee_code: res.data.employee_code,
          login_email: res.data.erp_login_email,
          temp_password: res.data.temp_password,
          login_url: loginUrl,
        });
      }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const editMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/employees/${id}`, payload),
    onSuccess: () => { toast.success("Employee updated"); qc.invalidateQueries({ queryKey: ["employees"] }); setEditing(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const actMut = useMutation({
    mutationFn: ({ id, action }) => api.post(`/employees/${id}/${action}`),
    onSuccess: (res, vars) => {
      toast.success(`${vars.action.replace(/-/g, " ")} · done`);
      qc.invalidateQueries({ queryKey: ["employees"] });
      if (res.data?.temp_password) {
        const emp = rows.find((r) => r.id === vars.id);
        setWelcome({
          employee_name: emp?.name || "Employee",
          employee_code: emp?.employee_code,
          login_email: res.data.login_email || emp?.erp_login_email,
          temp_password: res.data.temp_password,
          login_url: loginUrl,
        });
      }
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/employees/${id}`),
    onSuccess: () => { toast.success("Employee deleted"); setPendingDelete(null); qc.invalidateQueries({ queryKey: ["employees"] }); },
    onError: (e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setPendingDelete(null); },
  });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.salary === "") delete payload.salary;
    if (!payload.email) delete payload.email;
    if (!payload.reporting_manager_id) delete payload.reporting_manager_id;
    if (!payload.team_lead_id) delete payload.team_lead_id;
    if (!payload.branch) delete payload.branch;
    const code = (payload.employee_code || "").trim();
    if (code) payload.employee_code = code; else delete payload.employee_code;
    if (payload.create_erp_login) {
      const uname = (payload.login_username || "").trim().toLowerCase();
      if (uname) payload.erp_login_email = `${uname}@${loginDomain}`;
      if (payload.password_mode === "manual") {
        const pw = (payload.initial_password || "").trim();
        if (pw.length < 6) { toast.error("Initial password must be at least 6 characters."); return; }
        payload.initial_password = pw;
      } else {
        delete payload.initial_password;
      }
    } else {
      delete payload.erp_login_email;
      delete payload.initial_password;
    }
    delete payload.login_username;
    delete payload.password_mode;
    createMut.mutate(payload);
  };
  const submitEdit = (e) => {
    e.preventDefault();
    const payload = {};
    for (const k of ["name","designation","department","branch","email","phone","date_of_joining","salary","photo_url","reporting_manager_id","team_lead_id"]) {
      if (form[k] !== (editing?.[k] ?? "")) payload[k] = form[k];
    }
    if (payload.salary === "") delete payload.salary;
    editMut.mutate({ id: editing.id, payload });
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...EMPTY,
      name: e.name, designation: e.designation, department: e.department || "Design", branch: e.branch || "",
      email: e.email || "", phone: e.phone || "", date_of_joining: e.date_of_joining || "",
      salary: e.salary ?? "", photo_url: e.photo_url || "",
      reporting_manager_id: e.reporting_manager_id || "", team_lead_id: e.team_lead_id || "",
    });
  };

  useEffect(() => { if (addOpen) setForm(EMPTY); }, [addOpen]);

  return (
    <div data-testid="employees-page">
      <PageHeader
        eyebrow="Administration · People"
        title="Employees"
        description="Manage your team, ERP logins, and reporting structure. Every employee is one login inside the ERP."
        actions={
          <Sheet open={addOpen} onOpenChange={setAddOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-emp-btn"><Plus className="w-4 h-4 mr-1.5" /> New Employee</Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Employee</SheetTitle></SheetHeader>
              <form onSubmit={submit}>
                <EmployeeForm form={form} setForm={setForm} employees={rows} departments={departments} designations={designations} branches={branches} loginDomain={loginDomain} isEdit={false} />
                <SheetFooter className="mt-6">
                  <Button type="submit" disabled={createMut.isPending} data-testid="emp-submit">
                    {createMut.isPending ? "Creating…" : "Create Employee"}
                  </Button>
                </SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-1 border-b border-zinc-200" data-testid="emp-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              data-testid={`tab-${t.key || "active"}`}
            >{t.label}</button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input placeholder="Search by name / code / login…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="emp-search" />
          </div>
          <Select value={designationFilter || "all"} onValueChange={(v) => setDesignationFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="emp-filter-designation"><SelectValue placeholder="All designations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All designations</SelectItem>
              {designations.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={departmentFilter || "all"} onValueChange={(v) => setDepartmentFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="emp-filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branchFilter || "all"} onValueChange={(v) => setBranchFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-40" data-testid="emp-filter-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44" data-testid="emp-filter-role"><SelectValue placeholder="All ERP roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ERP roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid="emp-columns-trigger">
                <Columns3 className="w-3.5 h-3.5 mr-1.5" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="emp-columns-menu">
              <DropdownMenuLabel className="text-[10px] text-zinc-500 uppercase tracking-widest">Optional columns</DropdownMenuLabel>
              {OPTIONAL_COLS.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key}
                  checked={!!visibleCols[c.key]}
                  onCheckedChange={() => toggleCol(c.key)}
                  onSelect={(e) => e.preventDefault()}
                  className="text-xs"
                  data-testid={`emp-col-toggle-${c.key}`}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xs text-zinc-500 font-mono ml-auto" data-testid="emp-count">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>

        <div className="border border-zinc-200 rounded-lg bg-white overflow-x-auto" data-testid="emp-table">
          <table className="w-full text-sm min-w-[1180px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Photo</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Designation</th>
                <th className="text-left px-4 py-3">Department</th>
                <th className="text-left px-4 py-3">Branch / Team</th>
                {visibleCols.joining_date && <th className="text-left px-4 py-3">Joining Date</th>}
                {visibleCols.reporting_manager && <th className="text-left px-4 py-3">Reporting Manager</th>}
                {visibleCols.team_lead && <th className="text-left px-4 py-3">Team Lead</th>}
                {visibleCols.mobile && <th className="text-left px-4 py-3">Mobile</th>}
                {visibleCols.personal_email && <th className="text-left px-4 py-3">Personal Email</th>}
                {visibleCols.monthly_salary && <th className="text-left px-4 py-3">Monthly Salary</th>}
                {visibleCols.company && <th className="text-left px-4 py-3">Company</th>}
                {visibleCols.division && <th className="text-left px-4 py-3">Division</th>}
                <th className="text-left px-4 py-3">ERP Login</th>
                <th className="text-left px-4 py-3">ERP Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className={e.status === "archived" ? "opacity-60" : ""}>
                  <td className="px-4 py-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[10px] text-zinc-500">
                      {e.photo_url ? <img src={e.photo_url} alt="" className="w-full h-full object-cover" /> : (e.name || "?").slice(0, 1)}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.employee_code || "—"}</td>
                  <td className="px-4 py-3 font-medium">{e.name}</td>
                  <td className="px-4 py-3 text-[12px] text-zinc-700">{e.designation || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 text-[12px] text-zinc-700">{e.department || <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3 text-[12px] text-zinc-700">{e.branch || <span className="text-zinc-400">—</span>}</td>
                  {visibleCols.joining_date && <td className="px-4 py-3 text-[12px] text-zinc-700 font-mono">{e.date_of_joining || <span className="text-zinc-400">—</span>}</td>}
                  {visibleCols.reporting_manager && <td className="px-4 py-3 text-[12px] text-zinc-700">{e.reporting_manager_name || <span className="text-zinc-400">—</span>}</td>}
                  {visibleCols.team_lead && <td className="px-4 py-3 text-[12px] text-zinc-700">{e.team_lead_name || <span className="text-zinc-400">—</span>}</td>}
                  {visibleCols.mobile && <td className="px-4 py-3 text-[12px] text-zinc-700 font-mono">{e.phone || <span className="text-zinc-400">—</span>}</td>}
                  {visibleCols.personal_email && <td className="px-4 py-3 text-[12px] text-zinc-700 font-mono">{"email" in e ? (e.email || <span className="text-zinc-400">—</span>) : <span className="text-zinc-400 italic">restricted</span>}</td>}
                  {visibleCols.monthly_salary && <td className="px-4 py-3 text-[12px] text-zinc-700 font-mono">{"salary" in e ? (e.salary != null ? `₹${Number(e.salary).toLocaleString("en-IN")}` : <span className="text-zinc-400">—</span>) : <span className="text-zinc-400 italic">restricted</span>}</td>}
                  {visibleCols.company && <td className="px-4 py-3 text-[12px] text-zinc-700">{e.company_name || <span className="text-zinc-400">—</span>}</td>}
                  {visibleCols.division && <td className="px-4 py-3 text-[12px] text-zinc-700">{e.division_name || <span className="text-zinc-400">—</span>}</td>}
                  <td className="px-4 py-3">
                    <div className="text-xs font-mono">{e.erp_login_email || <span className="text-zinc-400">no login</span>}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {e.must_change_password && <span className="text-[10px] text-amber-600">first-login pending</span>}
                      {e.login_enabled === false && <span className="text-[10px] text-red-600">login disabled</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-zinc-600">{roleLabel(e.role)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`capitalize border text-[10px] ${STATUS_STYLES[e.status] || ""}`}>{(e.status || "").replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded-sm hover:bg-zinc-100" data-testid={`row-menu-${e.id}`}><MoreHorizontal className="w-4 h-4 text-zinc-500" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onSelect={() => openEdit(e)} data-testid={`edit-${e.id}`}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        {!e.user_id && (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "create-login" })} data-testid={`create-login-${e.id}`}>
                            <KeyRound className="w-3.5 h-3.5 mr-2" />Create ERP Login
                          </DropdownMenuItem>
                        )}
                        {e.user_id && (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "reset-password" })} data-testid={`reset-${e.id}`}>
                            <KeyRound className="w-3.5 h-3.5 mr-2" />Reset password
                          </DropdownMenuItem>
                        )}
                        {e.login_enabled ? (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "disable-login" })} data-testid={`disable-${e.id}`}>
                            <ShieldOff className="w-3.5 h-3.5 mr-2" />Disable login
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "enable-login" })} data-testid={`enable-${e.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5 mr-2" />Enable login
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "unlock-account" })} data-testid={`unlock-${e.id}`}>
                          <LockOpen className="w-3.5 h-3.5 mr-2" />Unlock account
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {e.status === "archived" ? (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "restore" })} data-testid={`restore-${e.id}`}>
                            <ArchiveRestore className="w-3.5 h-3.5 mr-2" />Restore
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={() => actMut.mutate({ id: e.id, action: "archive" })} data-testid={`archive-${e.id}`}>
                            <Archive className="w-3.5 h-3.5 mr-2" />Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onSelect={() => setPendingDelete(e)} className="text-red-600 focus:text-red-700" data-testid={`del-${e.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={totalColCount} className="py-12 text-center text-sm text-zinc-500">No employees match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle>Edit {editing?.name}</SheetTitle></SheetHeader>
          <form onSubmit={submitEdit}>
            <EmployeeForm form={form} setForm={setForm} employees={rows.filter((r) => r.id !== editing?.id)} departments={departments} designations={designations} branches={branches} loginDomain={loginDomain} isEdit={true} />
            <SheetFooter className="mt-6">
              <Button type="submit" disabled={editMut.isPending} data-testid="edit-submit">Save changes</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <WelcomeDialog open={!!welcome} onClose={() => setWelcome(null)} data={welcome} />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent data-testid="emp-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the employee <span className="font-mono">{pendingDelete?.employee_code}</span> and its ERP login. If they are linked to any project, production hour or team assignment, the delete will be blocked — archive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emp-del-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => pendingDelete && delMut.mutate(pendingDelete.id)}
              data-testid="emp-del-confirm"
            >
              {delMut.isPending ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
